import React, { useEffect, useRef, useState } from 'react'
import '@tamagui/core/reset.css'
import { Helmet } from 'react-helmet'
import {
  createBrowserRouter,
  RouterProvider, Outlet,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
  useNavigate
} from "react-router-dom";
import { APP_ENV } from '@my/config'
import { Provider } from 'app/provider'
import { RedirectToSignIn, SignIn } from '@clerk/react'
import { useUser } from '@my/util';
import { noRole, publicRoutesPathPrefixes, userRoutes } from './routes'
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://4b0d4bf8574ef18891030259de70f242@o4507966357110784.ingest.us.sentry.io/4507967032524800",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  //tracesSampleRate: 1.0,
  //tracePropagationTargets: ["localhost", APP_ENV.TZA_BACKEND_API_URI]
  environment: APP_ENV.TZA_ENV
});
// Call this AFTER Sentry.init()
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(
  createBrowserRouter,
);

const PUBLISHABLE_KEY = APP_ENV.CLERK_PUBLISHABLE_KEY

// MyApp Component
const MyApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useExternalUrlListener();
  return (
    <>
      <Helmet>
        <title>Parity | The Zillennial Advisors</title>
        <meta name="description" content="Revolutionizing Finance for a New Generation" />
        <link rel="icon" href="/favicon.ico" />
      </Helmet>
      {children}
    </>
  )
}

const getBrowserLocation = () => {
  const { pathname, search, hash, href } = window.location;
  return { pathname, search, hash, href };
};

const useCustomLocation = () => {
  const [location, setLocation] = useState(getBrowserLocation());

  useEffect(() => {
    const handlePopState = () => {
      setLocation(getBrowserLocation());
    };

    // Listen for changes in the URL triggered by browser navigation (back/forward buttons)
    window.addEventListener('popstate', handlePopState);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Empty dependency array ensures the effect runs only once on mount

  // You can add functions to programmatically change the URL if needed
  const push = (path) => {
    window.history.pushState({}, '', path);
    setLocation(getBrowserLocation()); // Update state after pushing new history entry
  };

  const replace = (path) => {
    window.history.replaceState({}, '', path);
    setLocation(getBrowserLocation()); // Update state after replacing history entry
  };

  return { ...location, push, replace };
};

// Route Setup Component
const RouteSetup: React.FC = () => {
  const { user, isSignedIn, isLoaded } = useUser();
  //const { isProcessing, hasTicket } = useHandleClerkTicket();
  const location = useCustomLocation();
  if (!isLoaded) {
    return null; // Keep showing loading while ticket is being consumed
  }
  const urlParams = new URLSearchParams(window.location.search);
  const ticket = urlParams.get('__clerk_ticket') as string;
  const dynamicTarget = urlParams.get("forceRedirectUrl") || urlParams.get("redirect_uri") || "/";
  if (ticket) {
    return <div style={{
      height: '100%',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <SignIn forceRedirectUrl={dynamicTarget} />
    </div>
  }
  const redirect = location.pathname+location.search+location.hash;
  const publicPath = !!publicRoutesPathPrefixes.find((pathPrefix) => location.pathname.startsWith(pathPrefix))
  if (!(publicPath || isSignedIn)) {
    return <RedirectToSignIn redirectUrl={publicPath ? redirect : '/'} />
  }
  const userType: any = user?.publicMetadata?.userType;

  const router = sentryCreateBrowserRouter([
    {
      element: (
        <MyApp>
          <Outlet />
        </MyApp>
      ),
      children: userRoutes//routes,
    },
  ])

  return <RouterProvider router={router} />
}

export function useExternalUrlListener() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  
  // Keep track of what React Router thinks the URL is
  const lastReactUrl = useRef(pathname + search + hash);

  useEffect(() => {
    // Update the ref whenever React Router changes the location internally
    lastReactUrl.current = pathname + search + hash;
  }, [pathname, search, hash]);

  useEffect(() => {
    const handleSync = () => {
      const currentNativeUrl = window.location.pathname + window.location.search + window.location.hash;
      
      // Only navigate if the browser URL differs from React Router's state
      if (currentNativeUrl !== lastReactUrl.current) {
        navigate(currentNativeUrl, { replace: true });
      }
    };

    // 1. Listen for Back/Forward buttons
    window.addEventListener('popstate', handleSync);

    // 2. Poll for manual history.pushState or location.hash changes
    const intervalId = setInterval(handleSync, 200);

    return () => {
      window.removeEventListener('popstate', handleSync);
      clearInterval(intervalId);
    };
  }, [navigate]); // No 'location' dependency here!
}

// Root Component
export const Root: React.FC = () => {
  const theme = 'light'

  return (
    <React.StrictMode>
      <Provider defaultTheme={theme} clerkPublishableKey={PUBLISHABLE_KEY}>
        <RouteSetup />
      </Provider>
    </React.StrictMode>
  )
}
